import config from '../config';
import apiService from '../utils/apiInterceptor';
import Cookies from 'js-cookie';
import userRolePermissionService from './userRolePermissionService';

const notifyPermissionsChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('permissionsChanged'));
  }
};

const clearStoredAuthContext = () => {
  localStorage.removeItem(config.AUTH_TOKEN_KEY);
  localStorage.removeItem('user');
  localStorage.removeItem('user_roles');
  localStorage.removeItem('user_roles_full');
  localStorage.removeItem('user_permissions');
  localStorage.removeItem('user_permissions_full');

  localStorage.removeItem('is_impersonating');
  localStorage.removeItem('_admin_token');
  localStorage.removeItem('_admin_user');
  localStorage.removeItem('_admin_roles');
  localStorage.removeItem('_admin_roles_full');
  localStorage.removeItem('_admin_permissions');
  localStorage.removeItem('_admin_permissions_full');
  localStorage.removeItem('_admin_return_path');
};

const authService = {
  login: async (email, password) => {
    clearStoredAuthContext();

    const response = await apiService.post('/loginapp', { email, password });
    if (response.token) {
      localStorage.setItem(config.AUTH_TOKEN_KEY, response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Load user roles and permissions
      if (response.user && response.user.id) {
        try {
          // Get user roles
          const rolesResponse = await userRolePermissionService.getUserRoles(response.user.id);
          if (rolesResponse.data && rolesResponse.data.roles) {
            const roleNames = rolesResponse.data.roles.map(role => role.name);
            localStorage.setItem('user_roles', JSON.stringify(roleNames));
          }
          
          // Get user permissions
          await userRolePermissionService.loadUserPermissions(response.user.id);
          notifyPermissionsChanged();
        } catch (error) {
          console.error('Error loading user roles and permissions:', error);
          notifyPermissionsChanged();
        }
      }
    }
    return response;
  },

  logout: async () => {
    try {
      // Only attempt logout API call if we have a token
      if (localStorage.getItem(config.AUTH_TOKEN_KEY)) {
        try {
          await apiService.post('/logout');
        } catch (apiError) {
          console.warn('Error during logout API call:', apiError);
          // Continue with cleanup even if API call fails
        }
      }
      
      // Clear all user data from localStorage
      clearStoredAuthContext();
      notifyPermissionsChanged();
      
      // Clear all cookies
      const cookies = Cookies.get();
      for (const cookie in cookies) {
        Cookies.remove(cookie);
      }
      
      return { success: true };
    } catch (err) {
      // Even on error, clear all user data
      clearStoredAuthContext();
      notifyPermissionsChanged();
      
      // Clear all cookies
      const cookies = Cookies.get();
      for (const cookie in cookies) {
        Cookies.remove(cookie);
      }
      
      console.error('Error logging out:', err);
      return { success: true }; // Return success anyway since we've cleared everything
    }
  },

  getCurrentUser: async () => {
    try {
      const user = localStorage.getItem('user');
      if (!user) return null;
      
      return JSON.parse(user);
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  },

  isAuthenticated: () => {
    return !!localStorage.getItem(config.AUTH_TOKEN_KEY);
  },
  
  /**
   * Load user roles and permissions after authentication
   * @param {number|string} userId - User ID
   * @returns {Promise<void>}
   */
  loadUserRolesAndPermissions: async (userId) => {
    try {
      // Skip if we don't have a valid user ID
      if (!userId) {
        console.warn('No user ID provided for loading roles and permissions');
        return;
      }
      
      // Get user roles
      const rolesResponse = await userRolePermissionService.getUserRoles(userId);
      if (rolesResponse && rolesResponse.data && rolesResponse.data.roles) {
        const roleNames = rolesResponse.data.roles.map(role => role.name);
        localStorage.setItem('user_roles', JSON.stringify(roleNames));
      } else {
        localStorage.setItem('user_roles', '[]');
      }
      
      // Get user permissions
      await userRolePermissionService.loadUserPermissions(userId);
      notifyPermissionsChanged();
    } catch (error) {
      console.error('Error loading user roles and permissions:', error);
      
      // Set empty arrays for roles and permissions on error
      localStorage.setItem('user_roles', '[]');
      localStorage.setItem('user_permissions', '[]');
      notifyPermissionsChanged();
    }
  },
  
  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} True if user has permission
   */
  hasPermission: (permission) => {
    // Get user permissions from localStorage
    const userPermissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Admin users have all permissions
    if (user.role === 'admin') {
      return true;
    }
    
    return userPermissions.includes(permission);
  }
};

export default authService;
